import React, { useCallback, useMemo, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { Box, Checkbox, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatDateHook } from "hooks";
import { useDateFormatSelect } from "store";
import { IFolder } from "store/types";

import { border, cellPx, templateColumns as cols, s } from "../styles";
import { FilePickerSelectionReturn } from "../hooks/use-file-picker-selection";

const INDENT = 32;
const headerSx = { fontSize: 14, fontWeight: 500, color: "#071F35", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...cellPx };
const cellSx = { fontSize: 14, color: "#071F35", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

interface FolderRowProps {
  folder: IFolder;
  selection: FilePickerSelectionReturn;
  investorMap: Map<number, string>;
  level: number;
}

const PickerFolder: React.FC<FolderRowProps> = ({ folder, selection, investorMap, level }) => {
  const [expanded, setExpanded] = useState(true);
  const formatDate = useFormatDateHook();
  const dateFormat = useDateFormatSelect();

  const directFiles = selection.resultFiles.filter((f) => f.section_id === folder.id);
  const childFolders = selection.resultFolders.filter((f) => f.parent_id === folder.id);
  const visibleFiles = directFiles.filter((f) => selection.matchesSearch(f) && selection.matchesFilters(f));
  const visibleChildFolders = childFolders.filter((f) => selection.folderHasMatchingFiles(f.id));

  const hasContent = visibleFiles.length > 0 || visibleChildFolders.length > 0;
  const isFiltering = !!selection.searchQuery || selection.hasActiveFilters;
  const shouldExpand = expanded || isFiltering;

  if (isFiltering && !hasContent) return null;

  const handleToggleExpand = useCallback(() => {
    if (hasContent) setExpanded((prev) => !prev);
  }, [hasContent]);

  return (
    <>
      {/* Folder row */}
      <Stack direction="row" alignItems="center" sx={{ ...s.folderRow, height: 32 }} onClick={handleToggleExpand}>
        <Box sx={{ width: 32 + level * INDENT, flexShrink: 0 }} />
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: "#071F35",
              transition: "transform 0.15s",
              transform: !shouldExpand ? "rotate(-90deg)" : "none",
              visibility: hasContent ? "visible" : "hidden"
            }}
          />
          <FolderOutlinedIcon sx={{ fontSize: 16, color: "#071F35" }} />
          <MaterialTooltip variant="dark" title={folder.title} placement="top" isShow={folder.title.length <= 35}>
            <Typography noWrap sx={{ fontSize: 14, color: "#071F35" }}>
              {folder.title}
            </Typography>
          </MaterialTooltip>
        </Box>
      </Stack>

      {/* Expanded children */}
      {shouldExpand && (
        <>
          {visibleChildFolders.map((child) => (
            <PickerFolder
              key={child.id}
              folder={child}
              selection={selection}
              investorMap={investorMap}
              level={level + 1}
            />
          ))}
          {visibleFiles.map((file) => {
            const selected = selection.isFileSelected(file.id);
            const disabled = selection.isSelectionDisabled(file.id);
            const investorName = file.investor_id ? investorMap.get(file.investor_id) : null;
            return (
              <Stack
                key={file.id}
                direction="row"
                alignItems="center"
                sx={{ ...(selected ? s.fileRowSelected : s.fileRow), height: 32 }}
                onClick={() => !disabled && selection.toggleFile(file.id, file.section_id)}
              >
                <Box
                  sx={{
                    width: 32 + (level + 1) * INDENT,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    px: "8px"
                  }}
                >
                  <Checkbox
                    checked={selected}
                    disabled={disabled}
                    onChange={() => selection.toggleFile(file.id, file.section_id)}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{ p: 0 }}
                  />
                </Box>
                <Box
                  sx={{
                    width: 380 - (level + 1) * INDENT,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    ...cellPx,
                    overflow: "hidden"
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: "#071F35", mr: "6px", flexShrink: 0 }} />
                  <MaterialTooltip variant="dark" title={file.name} placement="top">
                    <Typography noWrap sx={{ ...cellSx, color: "#071F35" }}>
                      {file.name}
                    </Typography>
                  </MaterialTooltip>
                </Box>
                <Box sx={{ ...cols.classification, ...cellPx, overflow: "hidden" }}>
                  {file.document_type ? (
                    <MaterialTooltip variant="dark" title={file.document_type} placement="top">
                      <Typography noWrap sx={cellSx}>
                        {file.document_type}
                      </Typography>
                    </MaterialTooltip>
                  ) : (
                    <Typography sx={{ fontSize: 14, color: "text.disabled", fontStyle: "italic" }}>
                      {"\u2014"}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ ...cols.investor, ...cellPx, overflow: "hidden" }}>
                  {investorName ? (
                    <MaterialTooltip variant="dark" title={investorName} placement="top">
                      <Typography noWrap sx={cellSx}>
                        {investorName}
                      </Typography>
                    </MaterialTooltip>
                  ) : (
                    <Typography sx={{ fontSize: 14, color: "#496682" }}>-</Typography>
                  )}
                </Box>
                <Box sx={{ ...cols.effectiveDate, ...cellPx }}>
                  <MaterialTooltip variant="dark" title={formatDate({ date: file.date, format: dateFormat })} placement="top">
                    <Typography noWrap sx={cellSx}>
                      {formatDate({ date: file.date, format: dateFormat })}
                    </Typography>
                  </MaterialTooltip>
                </Box>
                <Box sx={{ ...cols.uploadDate, ...cellPx }}>
                  <MaterialTooltip variant="dark" title={formatDate({ date: file.created_at, format: dateFormat })} placement="top">
                    <Typography noWrap sx={cellSx}>
                      {formatDate({ date: file.created_at, format: dateFormat })}
                    </Typography>
                  </MaterialTooltip>
                </Box>
              </Stack>
            );
          })}
        </>
      )}
    </>
  );
};

interface Props {
  selection: FilePickerSelectionReturn;
  investorMap: Map<number, string>;
}

export const TreeView: React.FC<Props> = ({ selection, investorMap }) => {
  const isFiltering = !!selection.searchQuery || selection.hasActiveFilters;

  const visibleParentFolders = useMemo(() => {
    if (!isFiltering) return selection.parentFoldersToRender;
    return selection.parentFoldersToRender.filter((f) => selection.folderHasMatchingFiles(f.id));
  }, [selection.parentFoldersToRender, isFiltering, selection]);

  return (
    <Box sx={{ border: border(), borderRadius: "8px", mx: "20px", my: "16px" }}>
      {/* Table header */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "background.default", borderBottom: border(), height: 32, borderRadius: "8px 8px 0 0" }}
      >
        <Box sx={{ ...cols.checkbox }} />
        <MaterialTooltip variant="dark" title="Name" placement="top">
          <Typography sx={{ ...cols.name, ...headerSx }}>Name</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Altius Classification" placement="top">
          <Typography sx={{ ...cols.classification, ...headerSx }}>Altius Classification</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Investor" placement="top">
          <Typography sx={{ ...cols.investor, ...headerSx }}>Investor</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Effective date" placement="top">
          <Typography sx={{ ...cols.effectiveDate, ...headerSx }}>Effective date</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Upload date" placement="top">
          <Typography sx={{ ...cols.uploadDate, ...headerSx }}>Upload date</Typography>
        </MaterialTooltip>
      </Stack>

      {/* Folder tree */}
      {visibleParentFolders.map((folder) => (
        <PickerFolder key={folder.id} folder={folder} selection={selection} investorMap={investorMap} level={0} />
      ))}

      {/* Empty state */}
      {visibleParentFolders.length === 0 && !selection.foldersLoading && (
        <Box sx={{ py: "56px", px: "20px", textAlign: "center" }}>
          {isFiltering ? (
            <>
              <SearchOffIcon sx={{ fontSize: 32, color: "text.disabled", display: "block", mx: "auto", mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                No files match the current filters.
              </Typography>
            </>
          ) : (
            <>
              <FolderOutlinedIcon sx={{ fontSize: 32, color: "text.disabled", display: "block", mx: "auto", mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No files in workspace</Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
